/**
 * Launches a modal with built-in accessibility support.
 *
 * Use ModalLauncher to manage the show/hide state for modal components, ensure
 * the focus is returned to the right place when the modal is dismissed, and
 * add a screen reader friendly message indicating that the link will open a
 * modal.
 */
import * as React from "react";

import {PageLifecycle} from "@repo/analytics-performance";
import {Errors, KAError} from "@repo/logging";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import deprecatedReactRender from "#rendering/deprecated-react-render.tsx";

type ClickHandler = (event?: React.SyntheticEvent | null | undefined) => void;
type CloseModalFn = () => void;
type ModalElement = React.ReactElement<any>;

type Props = {
    ariaLabel: string;
    children: (action: ClickHandler, arg2: string) => React.ReactNode;
    focusId: string;
    // The modal to display. Can either be a modal element, or a function that
    // returns a modal element when given a `closeModal` parameter.
    //
    // If you use the function syntax, then you must provide `closeModal` as
    // the `onClose` prop of the modal (or call it as part of your custom
    // `onClose` handler). Or, if you use the raw element syntax, then we clone
    // the modal and provide `closeModal` as the `onClose` prop automatically.
    modal: ModalElement | ((arg1: {closeModal: CloseModalFn}) => ModalElement);
    onClose?: () => void;
};

/**
 * @deprecated Use ModalLauncher from @khanacademy/wonder-blocks-modal instead.
 */
class DeprecatedModalLauncher extends React.Component<Props> {
    _modalContainer: HTMLDivElement | null | undefined;
    _unmountModal: (() => void) | undefined;

    // Be sure to remove the modal when the launcher unmounts!
    componentWillUnmount() {
        this.hideModal();
    }

    showModal: (event?: React.SyntheticEvent | null | undefined) => void = (
        event?: React.SyntheticEvent | null,
    ) => {
        // If an event was provided to this callback, prevent its default
        // behavior.
        //
        // TODO(mdr): How common is this? I'd expect that our modal launcher
        //     elements usually don't have default behavior, e.g., a button
        //     with `type="button"`. I'm not sure we want the ModalLauncher to
        //     be responsible for this.
        if (event) {
            event.preventDefault();
        }

        // Opening a modal counts a client navigation for the purposes of
        // timing the contents of the modal.
        PageLifecycle.handleStartRouterNavigation();

        if (!this._modalContainer) {
            // Create a new div element to contain our modal and append it to
            // the document body. Keep a reference so we can remove the modal
            // when it closes.
            const modalContainer = document.createElement("div");
            modalContainer.setAttribute("role", "dialog");

            if (!document.body) {
                throw new KAError(
                    "No body found in document!",
                    Errors.Internal,
                );
            }

            document.body.appendChild(modalContainer);

            let modalElement;
            const modalElementOrFunction = this.props.modal;
            if (typeof modalElementOrFunction === "function") {
                // If the modal prop is a function, we give it a `closeModal`
                // function, which it must connect to the `onClose` prop
                // manually (and optionally use to close the modal in response
                // to other events, like button presses).
                modalElement = modalElementOrFunction({
                    closeModal: this.hideModal,
                });
            } else {
                // Or, if the modal prop is an element, we automatically apply
                // `closeModal` as the `onClose` prop via cloning.
                modalElement =
                    modalElementOrFunction &&
                    React.cloneElement(modalElementOrFunction, {
                        onClose: this.hideModal,
                    });
            }

            this._unmountModal = deprecatedReactRender(
                modalElement,
                modalContainer,
            );

            this._modalContainer = modalContainer;
        }
    };

    hideModal: () => void = () => {
        const {focusId} = this.props;

        if (this._modalContainer) {
            const modalContainer = this._modalContainer;

            // Make sure to clear the reference to the modal container before
            // unmounting the modal, so we don't try to unmount it again in
            // componentWillUnmount.
            this._modalContainer = null;

            if (!document.body) {
                throw new KAError(
                    "No body found in document!",
                    Errors.Internal,
                );
            }

            this._unmountModal?.();
            this._unmountModal = undefined;
            document.body.removeChild(modalContainer);
        }

        const focusElement = document.getElementById(focusId);
        if (focusElement) {
            focusElement.focus();
        }
    };

    render(): React.ReactNode {
        const {children, ariaLabel} = this.props;

        return children(this.showModal, ariaLabel);
    }
}

export default DeprecatedModalLauncher;
